import { LinkButton } from "@/ui/next/LinkButton";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import localizeHref from "../../../lib/multilingual/localizeHref";
import { analyticsService } from "@/common/services/analytics/analytics.service";
import { NEW_IN_ROUTE } from "@/common/static/routes.static";
import HeroImage from "@/components/Homepage/assets/hero-brands-image.png";
import Image from "@/components/common/Image";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";

/**
 * The brands this banner sends people to: the design houses first, then the
 * fashion labels the artwork pictures. Marketing owns the list; changing it
 * changes the CTA destination and nothing else.
 *
 * Every slug here has to be a `brand` facet value — `/pages?url=/brands/<slug>`
 * resolves to `{ brand: [slug] }` for all fifteen. Eames is deliberately absent:
 * the catalogue files Eames under `designer` (`/designers/eames` runs
 * `{ designer: ["eames"] }`), and facets intersect with each other while values
 * OR inside one, so a `designer` param would narrow this list to Eames items
 * from these brands rather than widen it.
 */
const HERO_BRAND_SLUGS = [
  "vitra",
  "ligne-roset",
  "knoll",
  "bb-italia",
  "artifort",
  "louis-poulsen",
  "artemide",
  "hay",
  "foscarini",
  "flos",
  "kartell",
  "moooi",
  "gucci",
  "louis-vuitton",
  "dior",
];

/**
 * The brand filter is multi-value: one `brand` param per slug. The listing page
 * turns them into a single OR filter, so this lands on every item from any of
 * these brands, with each brand shown as a removable chip.
 */
const HERO_BRANDS_ROUTE = `${NEW_IN_ROUTE}?${HERO_BRAND_SLUGS.map(
  slug => `brand=${slug}`
).join("&")}`;

export const HomepageHeroBlock = () => {
  const { t } = useTranslation();
  const { locale } = useRouter();

  const handleShopBrandsClick = () => {
    /** Trigger `hp_shop_brands` event. */
    analyticsService.trackHpShopBrands();
  };

  return (
    <section className="bg-gradient-to-b from-[#631921] to-[#ab374b] md:bg-gradient-to-r">
      <div className="grid gap-8 px-4 pt-12 md:grid-cols-[1fr_auto] md:items-center md:gap-6 md:px-6 md:pt-0 lg:px-12 xl:px-24">
        {/* Left side */}
        <div className="flex flex-col space-y-12 md:py-16">
          <div className="flex flex-col items-start space-y-3 lg:min-w-96">
            <Heading as="h2" size="h2" className="text-white">
              {t("homepage:homepage-hero-block-title-v5")}
            </Heading>
            <Body as="p" className="text-flavour-50">
              {t("homepage:homepage-hero-body-v2")}
            </Body>
          </div>
          {/* Link only localizes a path when it is told the source locale, and
              it defaults source to the current one. Without this the Dutch site
              links to /nl/new instead of /nl/nieuw. */}
          <LinkButton
            href={localizeHref(HERO_BRANDS_ROUTE, "en", locale)}
            color="ghost"
            size="large"
            fill
            className="md:w-64"
            onClick={handleShopBrandsClick}
          >
            {t("homepage:homepage-hero-shop-brands-cta")}
          </LinkButton>
        </div>
        {/* Right side image. The artwork is 958x538 with the brand labels spread
            out sideways, so the box follows that ratio and grows in steps: at md
            a wider box would leave the headline no room, and a fluid one would
            push the band far taller than the design.

            The ratio comes from @tailwindcss/aspect-ratio, because this repo
            disables Tailwind's own aspect-ratio core plugin — so `aspect-[w/h]`
            silently produces no CSS here and collapses the box to zero height.
            16/9 is within 0.2% of 958/538, and `contain` absorbs the rest. */}
        <div className="aspect-h-9 aspect-w-16 relative w-full md:w-72 md:justify-self-end lg:w-96 xl:w-[30rem]">
          {/*
           * This is the mobile LCP element. `priority` makes next/image emit
           * a preload link and drop lazy loading; `fetchPriority` is a
           * separate prop in next/image and must be set explicitly, or the
           * browser queues the hero behind every other image on the page
           * (WHO-5478).
           */}
          <Image
            src={HeroImage}
            alt={t("homepage:homepage-hero-image-alt")}
            layout="fill"
            objectFit="contain"
            objectPosition="bottom center"
            sizes="(max-width: 767px) 100vw, (max-width: 1023px) 288px, (max-width: 1279px) 384px, 480px"
            priority
            fetchPriority="high"
          />
        </div>
      </div>
    </section>
  );
};
